import type { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { RegisterBodySchema } from '../lib/api/auth.gen';
import { OAuthProviderButtons } from '../components/auth/OAuthProviderButtons';
import { ChoiceGroup, Form, FormError, FormField, FormSubmit, Input, Stack, useZodForm } from '../components/ui';

// G-26 (story 2-95b): composed over the generated contract schema
// (`RegisterBodySchema`), not hand-declared. The wire contract's own
// `.refine()` (accountType==='organization' requires orgName) has no `path`,
// so a violation would attach to the form ROOT rather than any one
// `FormField` — this adds a SECOND, path-scoped refine on top so the error
// actually renders under the Organization name field a user can see and fix,
// same composition idiom AcceptInvitePage's own `.refine()` already uses.
const signupFormSchema = RegisterBodySchema.refine(
  (values) => values.accountType !== 'organization' || Boolean(values.orgName?.trim()),
  { message: 'Organization name is required.', path: ['orgName'] },
);

export function SignupPage(): JSX.Element {
  const { signup, error, loading } = useAuth();
  const [searchParams] = useSearchParams();
  // Pricing "Go Pro" links to /signup?plan=pro — preselect Organization + Pro.
  const planParam = searchParams.get('plan');
  const form = useZodForm(signupFormSchema, {
    defaultValues: {
      name: '',
      email: '',
      password: '',
      accountType: planParam === 'pro' ? 'organization' : 'personal',
      // `undefined`, not `''` — `orgName` is optional (`z.string().min(1)...
      // .optional()`), and zod's `.optional()` only accepts `undefined`, not
      // an empty string, which still runs through `.min(1)` and fails. A
      // personal signup silently submitted `orgName: ''` and got rejected by
      // the resolver on a field that isn't even rendered for that account
      // type — story-2-95a.spec.mjs check A caught this live (signup never
      // fired a request at all).
      orgName: undefined,
      planSlug: planParam === 'pro' ? 'pro' : 'free',
    },
  });
  const accountType = form.watch('accountType');
  const planSlug = form.watch('planSlug');

  const handleSubmit = async (values: z.input<typeof signupFormSchema>) => {
    await signup({
      name: values.name,
      email: values.email,
      password: values.password,
      accountType: values.accountType ?? 'personal',
      ...(values.accountType === 'organization'
        ? { orgName: values.orgName ?? '', planSlug: values.planSlug ?? 'free' }
        : {}),
    });
  };

  return (
    // Same deliberate `max-w-md` exception LoginPage.tsx's own header comment
    // documents — this page also renders inside PublicLayout's chrome.
    <Stack gap={8} className="mx-auto max-w-md">
      <Stack gap={3} className="text-center">
        <h1 className="text-3xl font-semibold text-foreground">Create your account</h1>
        <p className="text-sm text-muted-foreground">
          Start free — run AI inspections and export reports. No time limit, upgrade when you’re ready.
        </p>
      </Stack>

      <Form form={form} onSubmit={handleSubmit} className="rounded-hairline border border-border bg-card space-y-4 p-6">
        <FormField name="name" label="Full name" required>
          {(field) => <Input type="text" className="w-full" placeholder="Avery Shaw" {...field} />}
        </FormField>

        <FormField name="email" label="Work email" required>
          {(field) => <Input type="email" className="w-full" placeholder="you@agency.com" {...field} />}
        </FormField>

        <FormField
          name="password"
          label="Password"
          required
          description="Use at least 8 characters with a mix of letters and numbers."
        >
          {(field) => <Input type="password" className="w-full" placeholder="Create a strong password" {...field} />}
        </FormField>

        {/* Signup fork (story 2-13): personal solo workspace vs a named
            organization. `ChoiceGroup` (the 7th action host, story 2-147xd,
            ActionHosts.tsx), not `SegmentedControl` — a deliberate exception
            already established by story 2-147wf: `SegmentedControl`'s
            `role="radio"`/`aria-checked` contract would break
            `story-2-95a.spec.mjs`'s own `role="button"`/`aria-pressed`
            assertion outright, and this is a one-shot fork revealing
            different fields, not a view-mode switch over the same content.
            Same equal-width grid, `aria-pressed`, `type="button"` as the
            raw `Grid` + `Button` pair this replaces — now descriptor-driven. */}
        <Stack gap={2}>
          <span className="text-sm font-medium text-foreground">What are you setting up?</span>
          <ChoiceGroup
            aria-label="Account type"
            choices={[
              {
                id: 'personal',
                label: 'Personal',
                variant: accountType === 'personal' ? 'primary' : 'secondary',
                pressed: accountType === 'personal',
                onClick: () => form.setValue('accountType', 'personal', { shouldValidate: form.formState.isSubmitted }),
              },
              {
                id: 'organization',
                label: 'Organization',
                variant: accountType === 'organization' ? 'primary' : 'secondary',
                pressed: accountType === 'organization',
                onClick: () => form.setValue('accountType', 'organization', { shouldValidate: form.formState.isSubmitted }),
              },
            ]}
          />
          <p className="text-xs text-muted-foreground">
            {accountType === 'personal'
              ? 'A solo workspace — just your properties and inspections. Free, no time limit.'
              : 'A shared organization you own — invite your team and pick a plan.'}
          </p>
        </Stack>

        {accountType === 'organization' && (
          <>
            <FormField name="orgName" label="Organization name" required>
              {(field) => <Input className="w-full" placeholder="Acme Inspections" {...field} />}
            </FormField>

            <Stack gap={2}>
              <span className="text-sm font-medium text-foreground">Plan</span>
              <ChoiceGroup
                aria-label="Plan"
                choices={[
                  {
                    id: 'free',
                    label: 'Free',
                    variant: planSlug === 'free' ? 'primary' : 'secondary',
                    pressed: planSlug === 'free',
                    onClick: () => form.setValue('planSlug', 'free'),
                  },
                  {
                    id: 'pro',
                    label: 'Pro',
                    variant: planSlug === 'pro' ? 'primary' : 'secondary',
                    pressed: planSlug === 'pro',
                    onClick: () => form.setValue('planSlug', 'pro'),
                  },
                ]}
              />
              <p className="text-xs text-muted-foreground">
                {planSlug === 'pro'
                  ? 'Your organization starts on Free; you’ll finish payment at checkout right after signup.'
                  : 'Start free — upgrade to Pro anytime from Billing.'}
              </p>
            </Stack>
          </>
        )}

        <FormError message={error} />

        <FormSubmit className="w-full" disabled={loading} pendingLabel="Creating account…">
          {accountType === 'organization' && planSlug === 'pro'
            ? 'Create account & continue to payment'
            : 'Create account'}
        </FormSubmit>

        <OAuthProviderButtons intent="signup" />
      </Form>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="text-primary hover:underline">
          Log in
        </Link>
        .
      </p>
    </Stack>
  );
}
